import React, { useMemo } from 'react'
import { Autocomplete, Box, IconButton, TextField, Typography } from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'
import { validateCarrierGroup } from 'src/services/analysisStart'
import { CELL_ROW_ATTR, ErrorLine, Muted, TextLink } from '../components/ui'
import { candidateInfo } from '../model/candidates'
import { carrierSlotLabels, newCarrierGroup, setSlot } from './model'

// The samples area of a Carrier analysis (behind FEATURES.carrier): one row per
// couple (or group), one slot per sample the panel needs. A slot takes a ready
// SNV/Indel VCF; FASTQ pairs only with FEATURES.carrierFastq.

const CARRIER_ISSUE_TEXT = {
  no_config: 'Choose a carrier panel',
  wrong_count: 'Fill every slot',
  no_file: 'Fill every slot',
  duplicate: 'Same file twice',
  fastq_unavailable: 'FASTQ files cannot run carrier screening yet',
}

const isSnvVcf = (candidate) => (candidate.rows?.[0]?.variant_type || 'snp_indel') === 'snp_indel'

/** Uploads a carrier slot can take: ready SNV/Indel VCFs, and FASTQ pairs when allowed. */
export const carrierOptions = (candidates, { allowFastq = false } = {}) =>
  (Array.isArray(candidates) ? candidates : [])
    .filter((c) => {
      const info = candidateInfo(c)
      if (info.disabledReason || info.state !== 'ready') return false
      if (c.fileType === 'vcf') return isSnvVcf(c)
      return allowFastq && c.fileType === 'fastq' && !c.laneGroup
    })
    .map((c) => ({ fileId: c.id, fileType: c.fileType === 'vcf' ? 'VCF' : 'FASTQ', key: c.key }))

/** Problems with one couple, in the panel's words. */
export const carrierIssues = (group, config, { allowFastq = false } = {}) => {
  const raw = validateCarrierGroup({
    config: config ? { id: config.id, samples_required: config.samplesRequired } : null,
    members: group.members,
    allowFastq,
  })
  const seen = new Set()
  return raw
    .filter((i) => i && i.blocking !== false)
    .map((i) => CARRIER_ISSUE_TEXT[i.code] || i.message || 'Not ready')
    .filter((text) => (seen.has(text) ? false : seen.add(text)))
}

// A file chosen earlier (a restored draft) stays shown even when the list no longer offers it.
const withCurrent = (options, member) =>
  member && !options.some((o) => o.fileId === member.fileId) ? [member, ...options] : options

const SlotCell = ({ label, member, options, taken, onChange }) => (
  <Autocomplete
    size="small"
    options={withCurrent(options, member)}
    value={member}
    getOptionLabel={(o) => o?.key || ''}
    isOptionEqualToValue={(o, v) => o.fileId === v?.fileId}
    getOptionDisabled={(o) => taken.has(o.fileId) && o.fileId !== member?.fileId}
    onChange={(_e, value) => onChange(value || null)}
    renderOption={(props, o) => (
      <li {...props} key={o.fileId}>
        <Typography sx={{ fontFamily: 'fontFamilyMono', fontSize: 13.5, flex: 1, minWidth: 0 }} noWrap>
          {o.key}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', ml: 1 }}>{o.fileType}</Typography>
      </li>
    )}
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder="Choose a file"
        inputProps={{ ...params.inputProps, 'aria-label': label }}
        sx={{
          '& .MuiInputBase-root': { fontSize: 13.5, fontFamily: 'fontFamilyMono', height: 34, py: '0 !important' },
        }}
      />
    )}
  />
)

/**
 * @param groups   groupState.carrier.groups
 * @param config   { id, title, samplesRequired } from the workflow, or null
 * @param onChange (groups) => void
 */
const CarrierArea = ({ groups, config, candidates, allowFastq, showErrors, onChange }) => {
  const options = useMemo(() => carrierOptions(candidates, { allowFastq }), [candidates, allowFastq])
  const size = carrierSlotLabels(config?.samplesRequired).length
  // Shown until the first file is chosen, so there is always a row to fill.
  const placeholder = useMemo(() => newCarrierGroup(size), [size])

  if (!config?.id) {
    return (
      <Box sx={{ maxWidth: 880, px: 1, py: 2 }}>
        <Muted>Choose a carrier panel to add couples.</Muted>
      </Box>
    )
  }

  const labels = carrierSlotLabels(config.samplesRequired)
  const grid = `repeat(${labels.length}, minmax(0, 1fr)) 32px`
  const rows = groups.length ? groups : [placeholder]
  const update = (uid, next) => onChange(rows.map((g) => (g.uid === uid ? next : g)))
  const noun = labels.length === 2 ? 'couple' : 'group'

  return (
    <Box sx={{ maxWidth: 880 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: grid,
          columnGap: 1.5,
          pb: 0.5,
          '& > *': { fontSize: 12.5, color: 'text.secondary', fontWeight: 500 },
        }}
      >
        {labels.map((l) => (
          <Typography key={l}>{l}</Typography>
        ))}
        <span />
      </Box>
      {rows.map((group, index) => {
        const taken = new Set(group.members.filter(Boolean).map((m) => m.fileId))
        const issues = carrierIssues(group, config, { allowFastq })
        return (
          <Box key={group.uid} sx={{ mb: 1.5 }}>
            <Box
              {...{ [CELL_ROW_ATTR]: '' }}
              sx={{ display: 'grid', gridTemplateColumns: grid, columnGap: 1.5, alignItems: 'center', minHeight: 44 }}
            >
              {labels.map((label, slot) => (
                <SlotCell
                  key={label}
                  label={`${label}, ${noun} ${index + 1}`}
                  member={group.members[slot] || null}
                  options={options}
                  taken={taken}
                  onChange={(member) => update(group.uid, setSlot(group, slot, member))}
                />
              ))}
              <IconButton
                size="small"
                aria-label={`Remove ${noun} ${index + 1}`}
                onClick={() => onChange(rows.filter((g) => g.uid !== group.uid))}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            {showErrors && issues.length > 0 && <ErrorLine sx={{ mt: 0.25 }}>{issues.join('. ')}</ErrorLine>}
          </Box>
        )
      })}
      <TextLink onClick={() => onChange([...rows, newCarrierGroup(labels.length)])}>Add a {noun}</TextLink>
      {!options.length && <Muted sx={{ mt: 2 }}>No ready SNV/Indel VCF uploads yet.</Muted>}
    </Box>
  )
}

export default CarrierArea
